import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, EventEmitter, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CheckboxComponent } from '@app/shared/components/checkbox/checkbox.component';
import { TabContainerComponent, TabViewModel } from '@app/shared/components/tab-container/tab-container.component';
import { ToggleComponent } from '@app/shared/components/toggle/toggle.component';
import { IconBagComponent } from '@app/shared/icons/components/icon-bag/icon-bag.component';
import { CountryModel } from '@models/country-model';
import { CustomerModel } from '@models/customer';
import { OrdersDataService } from '@services/orders-data.service';
import { SalesDistrictType } from '@models/sales-district';
import { TranslatePipe } from '@ngx-translate/core';
import { CustomerDataService } from '@services/customer-data.service';
import { HttpService } from '@services/http.service';
import { LanguageService } from '@services/language.service';
import { OrderOnBehalfService } from '@services/order-on-behalf.service';
import * as i18nIsoCountries from 'i18n-iso-countries';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { BehaviorSubject, debounceTime, Subject } from 'rxjs';
import { UserDataModel } from '@models/user-data-model';
import { EventsService } from '../../_services/events.service';
import { UserService } from '../../_services/user.service';

@Component({
	selector: 'cstmsr-customer-selector',
	templateUrl: 'customer-selector.component.html',
	styleUrls: ['./customer-selector.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [InfiniteScrollDirective, TranslatePipe, ToggleComponent, CheckboxComponent, CommonModule, FormsModule, TabContainerComponent, IconBagComponent]
})
export class CustomerSelectorComponent implements OnInit {

	customerSelectorService = inject(OrderOnBehalfService);
	router = inject(Router);
	breakpointObserver = inject(BreakpointObserver);

	customerChange: EventEmitter<CustomerModel | null> = new EventEmitter();
	public allCustomers$ = new BehaviorSubject<Array<CustomerModel>>([]);

	public customersOwnDistrict$ = new BehaviorSubject<Array<CustomerModel>>([]);
	public customersOtherDistricts$ = new BehaviorSubject<Array<CustomerModel>>([]);
	private loadingOrderIds = new Set<string>();

	public shopInfoCustomers: Array<CustomerModel> | null;
	public countryCodes: Array<CountryModel> = [];

	public user!: UserDataModel | null;
	public isReady: boolean = false;
	public showOtherDistricts: boolean = false;
	public showExPartners = false;
	public showPreciousPartners = false;
	public districtClass: string = '';
	public searchString: string = '';
	public newCustomersOptionGroupCount?: number
	public isDoingShopInfoLookup: boolean = false;

	public newCustomerModuleEnabled: boolean = false
	public debouncer: Subject<string> = new Subject<string>();
	@ViewChild('input', { static: false }) input: ElementRef<HTMLInputElement>;

	showActiveCustomers: boolean = true;
	showNonPartnerCustomers: boolean = false;
	showBlockedCustomers: boolean = false;
	showNonDistributedCustomers: boolean = false;

	activeCustomerColor: string = '#456E57';
	nonPartnerCustomerColor: string = '#E2BE3F';
	blockedCustomerColor: string = '#D36B41';
	nonDistributedCustomerColor: string = '#6FC9E3';

	offset: number = 0;
	limit: number = 100;
	onlyWithActiveOrders: boolean = false;

	customerStatusEnum = CustomerStatusEnum;

	public searchTerm: Subject<string | null> = new Subject<string | null>();
	oldSearchString: string | null = null;
	CustomerSelectorTabNameOptions = CustomerSelectorTabName;
	searchParams: CustomerStatusEnum[] = [];

	public firstLookUpDone: boolean = false;

	tabs: TabViewModel[] = [];

	activeTab: TabViewModel;

	toggleMini: boolean = false;

	destroyRef = inject(DestroyRef);

	constructor(
		private userService: UserService,
		private eventService: EventsService,
		private customerDataService: CustomerDataService,
		private ordersDataService: OrdersDataService,
		public httpService: HttpService,
		public languageService: LanguageService,
	) {
		this.breakpointObserver
			.observe(`(max-width: 680px)`)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((state) => {
				if (!state.matches) {
					this.toggleMini = false;
				} else {
					this.toggleMini = true;
				}
			});
	}

	async ngOnInit() {
		this.eventService.showLoader(true);
		this.user = await this.userService.GetUser();
		this.newCustomerModuleEnabled = await this.userService.NewCustomerModuleEnabled() ?? false;

		this.searchTerm.pipe(debounceTime(800)).subscribe(async s => await this.filterCustomers(s));

		setTimeout(() => {
			this.isReady = true;
			this.eventService.showLoader(false);
		}, 1000);

		let display = this.languageService.instant('CUSTOMERS.OWN-DISTRICT');

		if (this.user?.AdminOnChain) {
			display = this.languageService.instant('CUSTOMERS.MY-SHOPS');
		}

		const tab: TabViewModel = {
			name: CustomerSelectorTabName.my,
			display,
			theme: 'titanium'
		};

		this.tabs.push(tab);

		this.activeTab = tab;

		this.searchParams.push(CustomerStatusEnum.active);
		await this.doSearchCustomers();
	}

	async doSearchCustomers() {
		const chunk = await this.customerDataService.getCustomers(false, true, this.searchParams, this.showPreciousPartners, this.offset, this.limit, this.searchString);

		const updatedCustomers = [...this.allCustomers$.value, ...chunk];
		this.allCustomers$.next(updatedCustomers);

		if (this.user?.AdminOnChain) {
			this.customersOwnDistrict$.next(updatedCustomers.filter(
				(s) => (s.Status === CustomerStatusEnum.active || s.Status === CustomerStatusEnum.nonPartner)
			));
		}
		else {
			this.customersOwnDistrict$.next(updatedCustomers.filter(
				(x) => x.SalesDistrictType === SalesDistrictType.OwnDistrict
			));

			this.customersOtherDistricts$.next(updatedCustomers.filter(
				(x) => x.SalesDistrictType === SalesDistrictType.OtherDistricts
			));

			this.showOtherDistricts = this.customersOtherDistricts$.value.length > 0;

			if (this.showOtherDistricts) {
				let otherTabIdx = this.tabs.findIndex(t => t.name === CustomerSelectorTabName.other);

				const tp = typeof otherTabIdx === 'number';

				if (tp && otherTabIdx < 0) {
					const display = this.languageService.instant('CUSTOMERS.OTHER-DISTRICTS');
					const tab: TabViewModel = {
						name: CustomerSelectorTabName.other,
						display,
						theme: 'titanium'
					};

					this.tabs.push(tab);
				}

				this.districtClass = "district-mode";
			}
		}

		if (this.user?.AdminOnChain) {
			void this.loadOrderIdsInBackground(chunk);
		}
	}

	private async loadOrderIdsInBackground(customers: CustomerModel[]) {
		const customersWithoutOrderIds = customers.filter(customer => customer.OrderIds == null);

		customersWithoutOrderIds.forEach(customer => this.loadingOrderIds.add(customer.No));
		this.customersOwnDistrict$.next([...this.customersOwnDistrict$.value]);

		await Promise.all(customersWithoutOrderIds.map(async customer => {
			try {
				customer.OrderIds = await this.ordersDataService.getOrderIds(customer.No) ?? [];
			}
			catch {
				customer.OrderIds = [];
			}
			finally {
				this.loadingOrderIds.delete(customer.No);
				this.customersOwnDistrict$.next([...this.customersOwnDistrict$.value]);
			}
		}));
	}

	isLoadingOrderIds(customer: CustomerModel): boolean {
		return this.loadingOrderIds.has(customer.No);
	}

	private handleCustomers() {
		if (this.user?.AdminOnChain) {
			const filteredCustomers1 = this.allCustomers$.value.filter(
				(s) => (s.Status === CustomerStatusEnum.active || s.Status === CustomerStatusEnum.nonPartner)
			);

			this.customersOwnDistrict$.next([...filteredCustomers1]);
		}
		else {
			const filteredCustomers = this.allCustomers$.value.filter(
				(x) => x.SalesDistrictType === SalesDistrictType.OwnDistrict
			);

			this.customersOwnDistrict$.next([...filteredCustomers]);

			const filteredCustomers2 = this.allCustomers$.value.filter(
				(x) => x.SalesDistrictType === SalesDistrictType.OtherDistricts
			);

			this.customersOtherDistricts$.next([...filteredCustomers2]);

			this.showOtherDistricts = this.customersOtherDistricts$.value.length > 0;

			if (this.showOtherDistricts) {
				let otherTabIdx = this.tabs.findIndex(t => t.name === CustomerSelectorTabName.other);

				const tp = typeof otherTabIdx === 'number';

				if (tp && otherTabIdx < 0) {
					const display = this.languageService.instant('CUSTOMERS.OTHER-DISTRICTS');
					const tab: TabViewModel = {
						name: CustomerSelectorTabName.other,
						display,
						theme: 'titanium'
					};

					this.tabs.push(tab);
				}

				this.districtClass = "district-mode";
			}

		}
	}

	async onSelect(customer: CustomerModel | null) {
		this.customerSelectorService.setCustomer(customer);

		this.router.navigate(['/']);
	}

	hasOpenOrders(customer: CustomerModel): boolean {
		return customer.OrderIds?.length > 0 ? true : false;
	}

	async shopInfoLookup() {
		this.eventService.showLoader(true);

		this.isDoingShopInfoLookup = true;

		this.shopInfoCustomers = await this.customerDataService.getShopInfoCustomers();

		this.isDoingShopInfoLookup = false;
		this.firstLookUpDone = true;

		this.eventService.showLoader(false);

		if (this.shopInfoCustomers) {
			let leadsTabIdx = this.tabs.findIndex(t => t.name === CustomerSelectorTabName.leads);

			const tp = typeof leadsTabIdx === 'number';

			if (tp && leadsTabIdx < 0) {
				const display = this.languageService.instant('NEW.PARTNER.NEW.ACCOUNTS');
				const leadsTab: TabViewModel = {
					name: CustomerSelectorTabName.leads,
					display,
					theme: 'titanium'
				};
				this.tabs.push(leadsTab);

				leadsTabIdx = this.tabs.length - 1;

				this.activeTab = leadsTab;
			}


		}
	}

	loadLocale() {
		let localePromise;

		switch (this.languageService.currentLanguage) {
			case 'en':
				localePromise = import('i18n-iso-countries/langs/en.json');
				break;
			case 'fr':
				localePromise = import('i18n-iso-countries/langs/fr.json');
				break;
			case 'de':
				localePromise = import('i18n-iso-countries/langs/de.json');
				break;
			// Add cases for other languages as needed
			default:
				localePromise = import('i18n-iso-countries/langs/en.json');
		}

		localePromise.then((locale) => {
			i18nIsoCountries.registerLocale(locale);
		}).catch((error) => {
			console.error('Error loading locale:', error);
		});
	}

	onInputChange(event: Event): void {
		const inputElement = event.target as HTMLInputElement;
		this.searchTerm.next(inputElement.value);
	}

	public async ToggleExPartners(showEx: boolean) {
		this.eventService.showLoader(true);

		this.searchParams = [];

		if (showEx) {
			this.showActiveCustomers = !showEx;
			this.showNonPartnerCustomers = showEx;
			this.showBlockedCustomers = showEx;
			this.showNonDistributedCustomers = showEx;
			this.searchParams.push(CustomerStatusEnum.nonPartner);
			this.searchParams.push(CustomerStatusEnum.blocked);
			this.searchParams.push(CustomerStatusEnum.nonDistributed);
		}
		else {
			this.showActiveCustomers = showEx;
			this.showNonPartnerCustomers = !showEx;
			this.showBlockedCustomers = !showEx;
			this.showNonDistributedCustomers = !showEx;
			this.searchParams.push(CustomerStatusEnum.active);
		}

		this.showExPartners = showEx;

		this.allCustomers$.next([]);

		this.offset = 0;

		await this.doSearchCustomers();

		this.eventService.showLoader(false);
	}

	public async TogglePreciousPartners(showPrecious: boolean) {
		this.eventService.showLoader(true);
		this.allCustomers$.next([]);
		this.offset = 0;
		this.showPreciousPartners = showPrecious;
		await this.doSearchCustomers();

		this.eventService.showLoader(false);
	}

	public isChainAdmin() {
		if (!this.user || !this.user.AdminOnChain) {
			false;
		}

		return this.user?.AdminOnChain;
	}

	public showToggleExPartners() {
		return !this.isChainAdmin();
	}

	public showTogglePreciousPartners() {
		return !this.isChainAdmin();
	}

	public async filterCustomers(text: string | null) {
		if (text === '' || text === null || (text.length > 2 && text !== this.oldSearchString)) {
			setTimeout(async () => {
				this.searchString = text === null ? '' : text;

				this.oldSearchString = this.searchString;

				this.allCustomers$.next([]);
				this.offset = 0;

				await this.doSearchCustomers();

			}, 500);
		}
	}

	public onFocus() {
		setTimeout(() => {
			this.input.nativeElement.select();
			this.input.nativeElement.setSelectionRange(0, this.input.nativeElement.value.length);
		});
	}

	public activateTab(tab: TabViewModel) {
		if (this.activeTab !== tab) {
			this.activeTab = tab;
		}

		setTimeout(() => {
			this.scrollToActiveTabElement();
		}, 1)
	}

	private scrollToActiveTabElement() {
		let activeTabElement: HTMLElement | null = document.querySelector(".tab-list-item.active");
		//offset by 10 so the tab to the left is just visible (if any), so user knows they can still swipe/scroll
		if (activeTabElement)
			document.querySelector(".tabs")?.scrollTo(activeTabElement.offsetLeft - 10, 0);
	}

	async toggleCustomerType($event: boolean, customerStatus: CustomerStatusEnum) {
		if ($event && !this.searchParams.includes(customerStatus)) {
			this.searchParams.push(customerStatus);
		}
		else if (!$event && this.searchParams.includes(customerStatus)) {
			this.removeCustomerStatus(customerStatus);
		}

		this.allCustomers$.next([]);
		this.offset = 0;

		await this.doSearchCustomers();

	}

	removeCustomerStatus(customerStatus: CustomerStatusEnum) {
		this.searchParams.forEach((item, index) => {
			if (item === customerStatus) this.searchParams.splice(index, 1);
		});
	}

	async onScrollDown() {
		this.offset += this.limit;
		await this.doSearchCustomers();
	}

	toggleBrands(index: number, ownDistrict?: boolean) {
		if (ownDistrict) {
			const currentList = this.customersOwnDistrict$.value;

			if (index >= 0 && index < currentList.length) {
				const updatedList = [...currentList];

				updatedList[index] = {
					...currentList[index],
					ShowBrands: !currentList[index].ShowBrands,
				} as CustomerModel;

				this.customersOwnDistrict$.next(updatedList);
			}


		}
		else {

			const currentList = this.customersOtherDistricts$.value;

			if (index >= 0 && index < currentList.length) {
				const updatedList = [...currentList];

				updatedList[index] = {
					...currentList[index],
					ShowBrands: !currentList[index].ShowBrands,
				} as CustomerModel;

				this.customersOtherDistricts$.next(updatedList);
			}


		}
	}
}

export enum CustomerStatusEnum {
	'active' = 'Active',
	'blocked' = 'Blocked',
	'nonPartner' = 'NonPartners',
	'nonDistributed' = 'NonDistributed'
}

export enum CustomerSelectorTabName {
	'my' = 'my',
	'leads' = 'leads',
	'other' = 'other',
}